import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { ApiError, NetworkError } from '@/store/api/baseQuery';

// Error message dictionary (similar to admin-web)
export const ERROR_MESSAGES = {
  // Network errors
  NETWORK_ERROR: 'Unable to connect to server. Please check your internet connection.',
  TIMEOUT_ERROR: 'Request timed out. Please try again.',
  
  // Authentication errors
  UNAUTHORIZED: 'Your session has expired. Please log in again.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  
  // Server errors
  SERVER_ERROR: 'Server error occurred. Please try again later.',
  SERVICE_UNAVAILABLE: 'Service is temporarily unavailable. Please try again later.',
  
  // Client errors
  BAD_REQUEST: 'Invalid request. Please check your input and try again.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  
  // File upload errors
  UNSUPPORTED_FILE_TYPE: 'Unsupported file type. Please upload JPEG, PNG, GIF, or WebP images.',
  FILE_TOO_LARGE: 'File size exceeds limit. Please choose a smaller file.',
  
  // Resident list specific errors
  RESIDENT_LIST_FETCH_ERROR: 'Unable to load resident list. Please try again.',
  RESIDENT_DETAILS_FETCH_ERROR: 'Unable to load resident details. Please try again.',
  RESIDENT_UPDATE_ERROR: 'Unable to update resident information. Please try again.',
  
  // Default fallback
  DEFAULT: 'Something went wrong. Please try again or contact support.',
};

// Success message dictionary
export const SUCCESS_MESSAGES = {
  RESIDENT_UPDATED: 'Resident information updated successfully.',
  PAYMENT_TOGGLED: 'Payment settings updated successfully.',
  DEFAULT: 'Operation completed successfully.',
};

// Error types for better categorization
export enum ErrorType {
  NETWORK = 'NETWORK',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  VALIDATION = 'VALIDATION',
  SERVER = 'SERVER',
  CLIENT = 'CLIENT',
  UNKNOWN = 'UNKNOWN',
}

// Error severity levels
export enum ErrorSeverity {
  LOW = 'LOW',       // Minor issues, user can continue
  MEDIUM = 'MEDIUM', // Important issues, user should be aware
  HIGH = 'HIGH',     // Critical issues, user action required
  CRITICAL = 'CRITICAL', // System-level issues, immediate attention needed
}

// Enhanced error interface
export interface EnhancedError {
  type: ErrorType;
  severity: ErrorSeverity;
  message: string;
  originalError?: any;
  endpoint?: string;
  timestamp: Date;
  shouldRetry: boolean;
  retryCount?: number;
  userFriendlyMessage: string;
}

/**
 * Determines if an error is a FetchBaseQueryError
 */
export const isFetchBaseQueryError = (error: any): error is FetchBaseQueryError => {
  return error && typeof error === 'object' && 'status' in error;
};

/**
 * Determines if an error is a SerializedError
 */
export const isSerializedError = (error: any): error is SerializedError => {
  return error && typeof error === 'object' && 'message' in error && !('status' in error);
};

/**
 * Categorizes error based on status code or error type
 */
export const categorizeError = (error: any): ErrorType => {
  if (isFetchBaseQueryError(error)) {
    const status = error.status;
    
    if (typeof status === 'number') {
      if (status === 401) return ErrorType.AUTHENTICATION;
      if (status === 403) return ErrorType.AUTHORIZATION;
      if (status >= 400 && status < 500) return ErrorType.CLIENT;
      if (status >= 500) return ErrorType.SERVER;
    } else if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
      return ErrorType.NETWORK;
    }
  }
  
  if (isSerializedError(error)) {
    if (error.name === 'TypeError' && error.message?.includes('fetch')) {
      return ErrorType.NETWORK;
    }
  }
  
  return ErrorType.UNKNOWN;
};

/**
 * Determines error severity based on type and status
 */
export const getErrorSeverity = (error: any, errorType: ErrorType): ErrorSeverity => {
  if (errorType === ErrorType.AUTHENTICATION || errorType === ErrorType.AUTHORIZATION) {
    return ErrorSeverity.HIGH;
  }
  
  if (errorType === ErrorType.NETWORK) {
    return ErrorSeverity.MEDIUM;
  }
  
  if (isFetchBaseQueryError(error) && typeof error.status === 'number') {
    if (error.status >= 500) return ErrorSeverity.HIGH;
    if (error.status >= 400) return ErrorSeverity.MEDIUM;
  }
  
  return ErrorSeverity.LOW;
};

/**
 * Detects if an error is a network error (DNS, connection, timeout, etc.)
 */
export const isNetworkError = (error: any): boolean => {
  if (!error) return false;
  
  try {
    const errorString = JSON.stringify(error).toLowerCase();
    const message = error?.message?.toLowerCase() || '';
    const dataMessage = error?.data?.error?.message?.toLowerCase() || '';
    
    return (
      message.includes('enotfound') ||
      message.includes('etimedout') ||
      message.includes('econnrefused') ||
      message.includes('getaddrinfo') ||
      message.includes('network request failed') ||
      errorString.includes('enotfound') ||
      errorString.includes('etimedout') ||
      dataMessage.includes('enotfound') ||
      dataMessage.includes('etimedout')
    );
  } catch {
    return false;
  }
};

/**
 * Determines if an error should allow retry
 */
export const shouldAllowRetry = (error: any, errorType: ErrorType): boolean => {
  // Don't retry auth errors
  if (errorType === ErrorType.AUTHENTICATION || errorType === ErrorType.AUTHORIZATION) {
    return false;
  }
  
  // Don't retry client errors (4xx except 408, 429)
  if (isFetchBaseQueryError(error) && typeof error.status === 'number') {
    const status = error.status;
    if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
      return false;
    }
  }
  
  // Allow retry for network, server errors, and timeouts
  return errorType === ErrorType.NETWORK || errorType === ErrorType.SERVER || 
         error.status === 'TIMEOUT_ERROR' || error.status === 408 || error.status === 429;
};

/**
 * Gets user-friendly error message based on error type and context
 */
export const getUserFriendlyMessage = (
  error: any, 
  errorType: ErrorType, 
  endpoint?: string
): string => {
  // Check for server-provided error message first (multiple possible structures)
  if (isFetchBaseQueryError(error) && error.data && typeof error.data === 'object') {
    const errorData = error.data as any;
    
    // Try to extract message from various backend formats
    const backendMessage = 
      errorData.error?.message ||  // Nested error.message (most common backend format)
      errorData.message ||          // Direct message property
      errorData.error ||            // Error as string
      null;
    
    if (backendMessage && typeof backendMessage === 'string') {
      return backendMessage;
    }
  }
  
  // Check for network errors with more specific messages
  if (isNetworkError(error)) {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }
  
  // Endpoint-specific error messages
  if (endpoint) {
    if (endpoint.includes('/residents/list')) {
      return ERROR_MESSAGES.RESIDENT_LIST_FETCH_ERROR;
    }
    if (endpoint.includes('/residents/details')) {
      return ERROR_MESSAGES.RESIDENT_DETAILS_FETCH_ERROR;
    }
    if (endpoint.includes('/residents/update-resident')) {
      return ERROR_MESSAGES.RESIDENT_UPDATE_ERROR;
    }
  }
  
  // Generic error messages based on type
  switch (errorType) {
    case ErrorType.NETWORK:
      if (error.status === 'TIMEOUT_ERROR') {
        return ERROR_MESSAGES.TIMEOUT_ERROR;
      }
      return ERROR_MESSAGES.NETWORK_ERROR;
      
    case ErrorType.AUTHENTICATION:
      return ERROR_MESSAGES.UNAUTHORIZED;
      
    case ErrorType.AUTHORIZATION:
      return ERROR_MESSAGES.FORBIDDEN;
      
    case ErrorType.SERVER:
      if (isFetchBaseQueryError(error) && error.status === 503) {
        return ERROR_MESSAGES.SERVICE_UNAVAILABLE;
      }
      return ERROR_MESSAGES.SERVER_ERROR;
      
    case ErrorType.CLIENT:
      if (isFetchBaseQueryError(error)) {
        if (error.status === 400) return ERROR_MESSAGES.BAD_REQUEST;
        if (error.status === 404) return ERROR_MESSAGES.NOT_FOUND;
        if (error.status === 422) return ERROR_MESSAGES.VALIDATION_ERROR;
      }
      return ERROR_MESSAGES.BAD_REQUEST;
      
    default:
      return ERROR_MESSAGES.DEFAULT;
  }
};

/**
 * Creates an enhanced error object with all relevant information
 */
export const createEnhancedError = (
  error: any, 
  endpoint?: string, 
  retryCount = 0
): EnhancedError => {
  const errorType = categorizeError(error);
  const severity = getErrorSeverity(error, errorType);
  const shouldRetry = shouldAllowRetry(error, errorType);
  const userFriendlyMessage = getUserFriendlyMessage(error, errorType, endpoint);
  
  return {
    type: errorType,
    severity,
    message: error.message || 'Unknown error occurred',
    originalError: error,
    endpoint,
    timestamp: new Date(),
    shouldRetry,
    retryCount,
    userFriendlyMessage,
  };
};

/**
 * Logs error with appropriate level based on severity
 */
export const logError = (enhancedError: EnhancedError) => {
  const { severity, type, endpoint, userFriendlyMessage, originalError } = enhancedError;
  
  const logPrefix = `🔴 [${severity}] ${type} Error`;
  const logMessage = `${endpoint ? `${endpoint}: ` : ''}${userFriendlyMessage}`;
  
  switch (severity) {
    case ErrorSeverity.CRITICAL:
      console.error(logPrefix, logMessage, originalError);
      break;
    case ErrorSeverity.HIGH:
      console.error(logPrefix, logMessage);
      break;
    case ErrorSeverity.MEDIUM:
      console.warn(logPrefix, logMessage);
      break;
    case ErrorSeverity.LOW:
    default:
      console.log(logPrefix, logMessage);
      break;
  }
};

/**
 * Main error handling function that processes any error and returns enhanced error info
 */
export const handleApiError = (
  error: any, 
  endpoint?: string, 
  retryCount = 0
): EnhancedError => {
  const enhancedError = createEnhancedError(error, endpoint, retryCount);
  logError(enhancedError);
  return enhancedError;
};

/**
 * Utility to extract error message from RTK Query error
 */
export const getErrorMessage = (error: any): string => {
  const enhancedError = handleApiError(error);
  return enhancedError.userFriendlyMessage;
};

/**
 * Extracts the API error description from RTK Query error response
 * Checks multiple possible error structures
 */
export const getApiErrorDescription = (error: any, fallback?: string): string => {
  if (!error) return fallback || 'An error occurred. Please try again.';
  
  // RTK Query error structure
  const errorData = error?.data;
  
  // Try to get description from various error formats
  const description = 
    errorData?.error?.description || 
    errorData?.description || 
    errorData?.error?.message ||
    errorData?.message ||
    fallback ||
    'An error occurred. Please try again.';
  
  return description;
};

/**
 * Utility to check if error should show toast notification
 */
export const shouldShowToast = (error: any): boolean => {
  if (isFetchBaseQueryError(error)) {
    // Don't show toast for auth errors (handled by redirect)
    return ![401, 403].includes(error.status as number);
  }
  return true;
};
